import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {__} from '@vo/js/utils/i18n'

const initMapbox = () => {
    mapboxgl.accessToken = window.app?.mapboxToken
    if (!mapboxgl.accessToken) return

    const storeOverview = document.getElementById('store-overview')
    const storeLocatorMapElem = document.getElementById('store-locator-map')
    const searchStoresBtn = document.getElementById('store-locator-submit')
    const searchStoresInput = document.getElementById('store-locator-query')
    const myLocationBtn = document.getElementById('my-location-button')
    const noResultsElem = document.getElementById('store-locator-no-results')
    const categorySelect = document.getElementById('store-locator-category')

    if (!storeLocatorMapElem) return

    // Initialize data layer
    window.dataLayer = window.dataLayer || []

    const map = new mapboxgl.Map({
        container: storeLocatorMapElem,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [window.app.defaultLongitude, window.app.defaultLatitude],
        zoom: window.app.defaultZoom,
    })

    map.addControl(new mapboxgl.NavigationControl({ visualizePitch: false }))

    const markerSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path fill="#1d4ed8" d="M320 64C214 64 128 148.4 128 252.6C128 403.4 320 592 320 592C320 592 512 403.4 512 252.6C512 148.4 426 64 320 64z"/></svg>`

    // Source + layers for clustered points
    map.on('load', async () => {
        const markerImg = new Image(36, 36)
        markerImg.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markerSvg)
        await new Promise(resolve => { markerImg.onload = resolve })
        map.addImage('store-pin', markerImg)

        map.addSource('stores', {
            type: 'geojson',
            data: { type: 'FeatureCollection', features: [] },
            cluster: true,
            clusterRadius: 80,
            clusterMaxZoom: 18,
            generateId: true,
        })

        // Cluster bubbles
        map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: 'stores',
            filter: ['has', 'point_count'],
            paint: {
                'circle-radius': [
                    'step', ['get', 'point_count'],
                    16, 10, 20, 50, 28, 100, 36,
                ],
                'circle-color': '#3b82f6',
                'circle-opacity': 0.85,
            },
        })

        // Cluster counts
        map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: 'stores',
            filter: ['has', 'point_count'],
            layout: {
                'text-field': ['get', 'point_count_abbreviated'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
            },
            paint: { 'text-color': '#fff' },
        })

        // Individual points
        map.addLayer({
            id: 'unclustered-point',
            type: 'symbol',
            source: 'stores',
            filter: ['!', ['has', 'point_count']],
            layout: {
                'icon-image': 'store-pin',
                'icon-size': 1,
                'icon-anchor': 'bottom',
                'icon-allow-overlap': true,
            },
        })

        wireEvents()
        refresh() // initial load
    })

    // Click a cluster to zoom/expand
    map.addInteraction('click-clusters', {
        type: 'click',
        target: { layerId: 'clusters' },
        handler: (e) => {
            const feature = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })[0]
            const clusterId = feature.properties.cluster_id
            map.getSource('stores').getClusterExpansionZoom(clusterId, (err, zoom) => {
                if (err) return
                map.easeTo({ center: feature.geometry.coordinates, zoom })
            })
        }
    })

    let activePopup = null
    let activeCoordinates = null
    let centerOnClosest = false

    // Click a store to show popup
    map.addInteraction('click-unclustered-point', {
        type: 'click',
        target: { layerId: 'unclustered-point' },
        handler: (e) => {
            const coordinates = e.feature.geometry.coordinates.slice()
            const { title, address, phoneNumber, directionsUrl } = e.feature.properties

            if (
                activePopup &&
                activeCoordinates &&
                activeCoordinates[0] === coordinates[0] &&
                activeCoordinates[1] === coordinates[1]
            ) {
                activePopup.remove()
                activePopup = null
                activeCoordinates = null
                return
            }

            activePopup = openStorePopup(map, coordinates, title, address, phoneNumber, directionsUrl)
            activeCoordinates = coordinates

            window.dataLayer.push({
                event: 'map_pin_click',
                store_name: title,
                store_address: address,
                search_location: searchStoresInput.value,
                action_type: 'on_map'
            })
        }
    })

    // Cursor hints
    map.addInteraction('mouseenter-clusters', {
        type: 'mouseenter',
        target: { layerId: 'clusters' },
        handler: () => {
            map.getCanvas().style.cursor = 'pointer'
        }
    })
    map.addInteraction('mouseenter-unclustered-point', {
        type: 'mouseenter',
        target: { layerId: 'unclustered-point' },
        handler: () => {
            map.getCanvas().style.cursor = 'pointer'
        }
    })
    map.addInteraction('mouseleave-clusters', {
        type: 'mouseleave',
        target: { layerId: 'clusters' },
        handler: () => {
            map.getCanvas().style.cursor = ''
        }
    })
    map.addInteraction('mouseleave-unclustered-point', {
        type: 'mouseleave',
        target: { layerId: 'unclustered-point' },
        handler: () => {
            map.getCanvas().style.cursor = ''
        }
    })

    // Geolocation + search

    searchStoresBtn?.addEventListener('click', () => {
        axios.post(`/store-locator/location`, new URLSearchParams({ query: searchStoresInput.value }))
        .then(({ data }) => {
            if (data?.lat && data?.lng) {
                noResultsElem?.classList.add('hidden')
                centerOnClosest = true
                map.easeTo({ center: [data.lng, data.lat], zoom: 14 })
            } else {
                storeOverview.innerHTML = ''
                setGeojson([])
                noResultsElem?.classList.remove('hidden')
            }
        })
    })

    searchStoresInput?.addEventListener('keydown', (e) => {
        if (e.key.toLowerCase() === 'enter') searchStoresBtn.click()
    })

    myLocationBtn?.addEventListener('click', () => {
        navigator.geolocation.getCurrentPosition(async ({ coords }) => {
            const { latitude: lat, longitude: lng } = coords

            try {
                const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`)
                const data = await res.json()
                searchStoresInput.value = data.features.find(f => f.place_type.includes('place'))?.text
                searchStoresBtn.click()
            } catch (e) {
                console.error('Geocoding error:', e)
            }
        })
    })

    categorySelect?.addEventListener('change', () => {
        const url = new URL(window.location)
        if (categorySelect.value) {
            url.searchParams.set('category', categorySelect.value)
        } else {
            url.searchParams.delete('category')
        }
        window.history.pushState({}, '', url)

        refresh(true)
    })

    // Helper to open a popup for a store (used by the list items)
    const openStorePopup = (map, coordinates, title, address, phoneNumber, directionsUrl) => {
        if (activePopup) {
            activePopup.remove()
            activePopup = null
            activeCoordinates = null
        }
        const directionsLink = directionsUrl
            ? `
                <a class="font-roboto text-14 inline-flex items-center gap-1 shrink-0" href="${directionsUrl}" target="_blank" data-title="${title}" data-address="${address}" data-directions>
                    <svg class="icon icon--Distance_SF aspect-square fill-blue w-5 h-5" aria-hidden="true">
                        <use xlink:href="#omron-Distance_SF"></use>
                    </svg>
                    ${__('Get directions')}
                </a>
              `
            : ''

        const phoneLink = phoneNumber
            ? `
                <a class="font-roboto text-14 inline-flex items-center gap-1 shrink-0" href="tel:${phoneNumber}" target="_blank">
                    <svg class="icon icon--Call_S aspect-square fill-blue w-5 h-5" aria-hidden="true">
                        <use xlink:href="#omron-Call_S"></use>
                    </svg>
                    ${phoneNumber}
                </a>
              `
            : ''

        const links = (directionsLink || phoneLink)
            ? `<div class="flex gap-4 mt-2">${directionsLink}${phoneLink}</div>`
            : ''

        const popup = new mapboxgl.Popup({
            closeOnClick: false,
            closeOnMove: false,
            maxWidth: '300px',
            offset: [0, -36],
        })
            .setLngLat(coordinates)
            .setHTML(`
                <div id="store-location-popup" class="pr-4">
                    <h3 class="font-roboto text-18 font-700">${title}</h3>
                    <p class="font-roboto text-14">${address}</p>
                    ${links}
                </div>
            `)
            .addTo(map)

        popup.on('close', () => {
            if (activePopup === popup) {
                activePopup = null
                activeCoordinates = null
            }
        })

        const popupElement = document.getElementById('store-location-popup')
        popupElement.addEventListener('click', (e) => {
            const directionLink = e.target.closest('[data-directions]')
            if (directionLink) {
                const {title, address} = directionLink.dataset

                window.dataLayer.push({
                    event: 'get_directions_click_on_map',
                    store_name: title,
                    store_address: address,
                    search_location: searchStoresInput.value,
                    action_type: 'on_map'
                })
            }
        })

        return popup
    }

    // Refresh data when the map moves/zooms
    const wireEvents = () => {
        let loadedOnce = false
        let suppressRefresh = false

        map.on('moveend', () => {
            if (suppressRefresh) {
                suppressRefresh = false
                return
            }
            refresh()
        })
        map.on('idle', () => {
            if (!loadedOnce) {
                loadedOnce = true
                refresh()
            }
        })

        storeOverview.addEventListener('click', (e) => {
            const directionLink = e.target.closest('[data-directions]')
            if (directionLink) {
                const {title, address} = directionLink.previousElementSibling.dataset

                window.dataLayer.push({
                    event: 'store_locator_get_directions_click',
                    store_name: title,
                    store_address: address,
                    search_location: searchStoresInput.value,
                    action_type: 'on_search'
                })
            }

            const btn = e.target.closest('[data-view-on-map]')
            if (!btn) return

            const lng = parseFloat(btn.dataset.lng)
            const lat = parseFloat(btn.dataset.lat)
            if (!lng || !lat) return

            const { title, address, phoneNumber, directionsUrl } = btn.dataset
            const coordinates = [lng, lat]

            suppressRefresh = true
            map.flyTo({ center: coordinates, zoom: 15 })
            map.once('moveend', () => {
                activePopup = openStorePopup(map, coordinates, title, address, phoneNumber, directionsUrl)
                activeCoordinates = coordinates
            })

            window.dataLayer.push({
                event: 'store_locator_view_on_map_click',
                store_name: title,
                store_address: address,
                search_location: searchStoresInput.value,
                action_type: 'on_search'
            })
        })
    }

    // Fetch + set GeoJSON for current view
    const refresh = (withDataLayer = false) => {
        const zoom = map.getZoom()

        if (zoom < window.app.zoomLevelMin) {
            storeOverview.innerHTML = ''
            setGeojson([])
            showZoomOverlay()
            return
        }

        hideZoomOverlay()

        const center = map.getCenter()
        const bounds = map.getBounds()
        const ne = bounds.getNorthEast()
        const nw = new mapboxgl.LngLat(bounds.getWest(), ne.lat)

        // Width of the current view in meters (top edge)
        const mapWidthMeters = Math.round(ne.distanceTo(nw))
        const params = new URLSearchParams({
            zoom: String(Math.round(zoom)),
            distance: String(mapWidthMeters),
            lat: center.lat.toFixed(4),
            lng: center.lng.toFixed(4),
            location: searchStoresInput?.value || '',
            category: categorySelect?.value || '',
        })
        if (window.app.lang) params.append('lang', window.app.lang)
        if (window.app.filter) params.append('filter', window.app.filter)
        if (window.app.exclude) params.append('exclude', '1')

        axios.post('/store-locator/stores', params).then(({ data }) => {
            if (!data?.markers || data.markers.length === 0) {
                storeOverview.innerHTML = ''
                setGeojson([])

                noResultsElem?.classList.remove('hidden')

                if (withDataLayer) {
                    window.dataLayer.push({
                        event: 'store_locator_filter_applied',
                        filter_category: 'product_filter',
                        filter_value: categorySelect.options[categorySelect.selectedIndex].text,
                        search_location: searchStoresInput.value,
                        results_count: 0
                    })
                }
                return
            }

            // Hide "no results" message when results exist
            noResultsElem?.classList.add('hidden')

            // Convert markers array to GeoJSON
            const features = data.markers.map((s, i) => ({
                type: 'Feature',
                id: i,
                geometry: {
                    type: 'Point',
                    coordinates: [Number(s.longitude), Number(s.latitude)],
                },
                properties: {
                    title: s.title,
                    address: `${s.address}, ${s.postal_code}, ${s.city}`,
                    phoneNumber: s.phone_number,
                    directionsUrl: s.directions_url,
                },
            }))

            setGeojson(features)
            storeOverview.innerHTML = data.overview

            if (centerOnClosest) {
                centerOnClosest = false
                const closest = data.markers[0]
                map.easeTo({ center: [Number(closest.longitude), Number(closest.latitude)] })
            }

            if (withDataLayer) {
                window.dataLayer.push({
                    event: 'store_locator_filter_applied',
                    filter_category: 'product_filter',
                    filter_value: categorySelect.options[categorySelect.selectedIndex].text,
                    search_location: searchStoresInput.value,
                    results_count: data.markers.length
                })
            }
        })

    }

    const setGeojson = (features) => {
        const src = map.getSource('stores')
        if (!src) return
        src.setData({ type: 'FeatureCollection', features })
    }

    const showZoomOverlay = () => {
        const overlayParent = storeLocatorMapElem.querySelector('.mapboxgl-canvas-container')
        if (!overlayParent) return

        const currentOverlayElement = overlayParent.querySelector('#mapboxgl-zoom-overlay')
        if (!currentOverlayElement) {
            // Create overlay element
            const overlayElement = document.createElement('div')
            overlayElement.id = 'mapboxgl-zoom-overlay'
            overlayElement.innerHTML = `<p>${window.app.zoomInText}</p>`
            overlayParent.appendChild(overlayElement)
        } else {
            currentOverlayElement.style.opacity = '1'
        }
    }

    const hideZoomOverlay = () => {
        const overlayParent = storeLocatorMapElem.querySelector('.mapboxgl-canvas-container')
        if (!overlayParent) return

        const currentOverlayElement = overlayParent.querySelector('#mapboxgl-zoom-overlay')
        if (currentOverlayElement) {
            currentOverlayElement.style.opacity = '0'
        }
    }
}

export default (function () {
    const form = document.getElementById('store-locator-form')
    if (!form) return
    initMapbox()
})
